/**
 * Waiting on the page and on the server (spec 33 section 5.3).
 *
 * A walkthrough step that DOES something is finished when the thing has
 * happened, not when a timer says it probably has: the load exists when
 * POST /loads has answered 201, the row is on the board when the row is in
 * the DOM. Two primitives, both abortable, both answering null (never
 * throwing) when they give up, so the host always has a card to show.
 */

/**
 * The element `probe` names, now or as soon as the page renders it.
 *
 * @param {string|(() => Element|null)} probe a selector, or a function that finds it
 * @param {object} [options]
 * @param {number} [options.timeoutMs] give up after this long and answer null
 * @param {AbortSignal} [options.signal] give up now and answer null
 * @param {Element} [options.root] where to watch (document.body)
 * @returns {Promise<Element|null>}
 */
export function waitForElement(probe, { timeoutMs = 4000, signal = null, root = null } = {}) {
  const find =
    typeof probe === 'function' ? probe : () => document.querySelector(String(probe)) || null;
  const now = find();
  if (now) return Promise.resolve(now);
  if (signal && signal.aborted) return Promise.resolve(null);
  return new Promise(resolve => {
    let done = false;
    let observer = null;
    let timer = null;
    const finish = value => {
      if (done) return;
      done = true;
      if (observer) observer.disconnect();
      clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', onAbort);
      resolve(value);
    };
    const onAbort = () => finish(null);
    observer = new MutationObserver(() => {
      const el = find();
      if (el) finish(el);
    });
    observer.observe(root || document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });
    timer = setTimeout(() => finish(null), timeoutMs);
    if (signal) signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * A path pattern ('/loads/:id/invoice', or '/loads/{loadId}' already filled
 * in by the host) as a matcher for a request's path. The API's prefix and any
 * query string are ignored: '/api/loads/7?x=1' matches '/loads/:id'.
 */
export function pathMatcher(pattern) {
  const escaped = String(pattern)
    .split('/')
    .map(part => (part.startsWith(':') ? '[^/]+' : part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))
    .join('/');
  const re = new RegExp(`(?:^|/api)${escaped}/?$`);
  return path => re.test(path);
}

/** Just the path of a request URL, whether absolute, rooted or relative. */
export function pathOf(url) {
  const text = String(url || '');
  let path = text;
  try {
    path = new URL(text, 'http://x.invalid').pathname;
  } catch (e) {
    path = text.split('?')[0];
  }
  return path.startsWith('/') ? path : `/${path}`;
}

/** Does a status satisfy the step's expectation: a number, '2xx', or a list. */
export function statusMatches(expected, status) {
  if (expected === undefined || expected === null) return status >= 200 && status < 300;
  if (Array.isArray(expected)) return expected.some(e => statusMatches(e, status));
  if (typeof expected === 'string' && /^[1-5]xx$/.test(expected)) {
    return Math.floor(status / 100) === Number(expected[0]);
  }
  return Number(expected) === status;
}

/**
 * A log of the app's own API responses, kept only while a walkthrough is
 * running. It hangs one pair of interceptors on the app's HTTP client (the
 * global axios) and takes them off again when the walkthrough ends, so a real
 * tenant, where no walkthrough ever starts, never has them at all.
 *
 * Steps wait on it with `{ method, path, status }`. A response that does NOT
 * match the expected status but does match the method and path is reported
 * too (as `{ matched: false }`), because "the server said no" is an answer the
 * card has to give, and waiting out a timeout for it would be a stall.
 *
 * `observer` (filming only, training/render.js): `onRequest({ method, path })`
 * as each request leaves, returning an id, and `onResponse({ id, status })`
 * when its answer is in.
 */
export function createRequestLog(http, observer = null) {
  let ids = null;
  const waiters = new Set();

  const sent = config => {
    const body = config && config.data;
    if (body && typeof body === 'object') return body;
    try {
      return typeof body === 'string' ? JSON.parse(body) : null;
    } catch (e) {
      return null;
    }
  };

  const record = (config, status, data) => {
    if (observer && config && config.lwFilmId) {
      observer.onResponse({ id: config.lwFilmId, status: Number(status) || 0 });
    }
    const entry = {
      method: String((config && config.method) || 'get').toUpperCase(),
      path: pathOf(config && config.url),
      status: Number(status) || 0,
      data: data === undefined ? null : data,
      // What the page sent, for a step that needs to know what the visitor
      // typed (their own PO). Read in this tab only; never stored or sent on.
      body: sent(config),
    };
    for (const waiter of Array.from(waiters)) waiter(entry);
  };

  return {
    get installed() {
      return ids !== null;
    },

    install() {
      if (ids || !http || !http.interceptors) return;
      ids = {
        // An interceptor's id can be 0, so "none" is null, not falsy.
        request: !observer
          ? null
          : http.interceptors.request.use(config => {
              // eslint-disable-next-line no-param-reassign
              config.lwFilmId = observer.onRequest({
                method: String(config.method || 'get').toUpperCase(),
                path: pathOf(config.url),
              });
              return config;
            }),
        response: http.interceptors.response.use(
          response => {
            record(response && response.config, response && response.status, response.data);
            return response;
          },
          error => {
            const response = error && error.response;
            record(
              (error && error.config) || (response && response.config),
              response ? response.status : 0,
              response ? response.data : null
            );
            return Promise.reject(error);
          }
        ),
      };
    },

    uninstall() {
      if (!ids) return;
      http.interceptors.response.eject(ids.response);
      if (ids.request !== null) http.interceptors.request.eject(ids.request);
      ids = null;
      for (const waiter of Array.from(waiters)) waiter(null);
      waiters.clear();
    },

    /**
     * The next response to `method path`. Resolves `{ matched: true, entry }`
     * when its status is what the step expects, `{ matched: false, entry }`
     * when the server answered otherwise, and null on timeout or abort.
     */
    waitFor({ method = 'GET', path, status } = {}, { timeoutMs = 15000, signal = null } = {}) {
      const wantMethod = String(method).toUpperCase();
      const matchesPath = pathMatcher(path);
      return new Promise(resolve => {
        let timer = null;
        const finish = value => {
          waiters.delete(waiter);
          clearTimeout(timer);
          if (signal) signal.removeEventListener('abort', onAbort);
          resolve(value);
        };
        const onAbort = () => finish(null);
        function waiter(entry) {
          if (entry === null) return finish(null);
          if (entry.method !== wantMethod || !matchesPath(entry.path)) return;
          finish({ matched: statusMatches(status, entry.status), entry });
        }
        if (signal && signal.aborted) return resolve(null);
        waiters.add(waiter);
        timer = setTimeout(() => finish(null), timeoutMs);
        if (signal) signal.addEventListener('abort', onAbort, { once: true });
      });
    },
  };
}
